import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCamera, faLocationDot, faCoffee, faStar } from '@fortawesome/free-solid-svg-icons';
import { cn } from '../../../../ui/utils/cn';

interface StepIndicatorProps {
    currentStep: number;
    onStepClick?: (step: number) => void;
    disabled?: boolean;
    compact?: boolean;
    // optional per-step labels override (e.g. the supermarket flow: Photo·Brand·Type·Rate).
    // Defaults to the shop wizard's Image·Location·Coffee·Rating.
    labels?: [string, string, string, string];
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
    currentStep,
    onStepClick,
    disabled = false,
    compact = false,
    labels
}) => {
    const stepLabels = labels ?? ['Image', 'Location', 'Coffee', 'Rating'];
    const steps = [
        { num: 1, label: stepLabels[0], icon: faCamera },
        { num: 2, label: stepLabels[1], icon: faLocationDot },
        { num: 3, label: stepLabels[2], icon: faCoffee },
        { num: 4, label: stepLabels[3], icon: faStar },
    ];

    const handleStepClick = (stepNum: number) => {
        // only allow clicking on completed steps
        if (onStepClick && !disabled && stepNum < currentStep) {
            onStepClick(stepNum);
        }
    };

    return (
        <div className={cn(
            "flex items-center",
            compact ? "gap-1 justify-center" : "justify-center px-4 py-3 rounded-full bg-gray-100"
        )}>
            {steps.map((step, index) => {
                const isActive = currentStep === step.num;
                const isCompleted = currentStep > step.num;
                const isClickable = !disabled && isCompleted && onStepClick;

                return (
                    <div key={step.num} className="flex items-center">
                        <div 
                            className={cn(
                                "flex items-center",
                                compact ? "gap-0.5" : "flex-col gap-1 px-2 sm:px-3",
                                isClickable && "cursor-pointer group"
                            )}
                            onClick={() => handleStepClick(step.num)}
                            role={isClickable ? "button" : undefined}
                            tabIndex={isClickable ? 0 : undefined}
                            onKeyDown={(e) => {
                                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                                    handleStepClick(step.num);
                                }
                            }}
                        >
                            <div
                                className={cn(
                                    "flex items-center justify-center rounded-full transition-all duration-300",
                                    compact ? "h-7 w-7" : "h-10 w-10 sm:h-11 sm:w-11 text-sm",
                                    isActive && "bg-blue-500 text-white shadow-sm",
                                    isCompleted && "bg-gray-400 text-white",
                                    !isActive && !isCompleted && "bg-gray-200 text-gray-400",
                                    isClickable && "group-hover:scale-110 group-hover:bg-gray-500"
                                )}
                            >
                                {isCompleted ? (
                                    <FontAwesomeIcon icon={faCheck} className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
                                ) : (
                                    <FontAwesomeIcon icon={step.icon} className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
                                )}
                            </div>
                            {!compact && (
                                <span
                                    className={cn(
                                        "text-[10px] sm:text-xs font-medium transition-colors",
                                        isActive && "text-blue-600 font-semibold",
                                        isCompleted && "text-gray-500",
                                        !isActive && !isCompleted && "text-gray-400",
                                        isClickable && "group-hover:text-gray-600"
                                    )}
                                >
                                    {step.label}
                                </span>
                            )}
                        </div>
                        
                        {/* progress connector line */}
                        {index < steps.length - 1 && (
                            <div className={cn(
                                "relative rounded-full overflow-hidden bg-gray-200",
                                compact ? "h-0.5 w-4 mx-0.5" : "h-0.5 w-6 sm:w-10"
                            )}>
                                <div
                                    className={cn(
                                        "absolute inset-y-0 left-0 bg-gray-400 transition-all duration-500 rounded-full",
                                        isCompleted ? "w-full" : "w-0"
                                    )}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default StepIndicator;
